import React from "react";
import { authContext } from "../../containers/AuthProvider";
import Panel from "../Panel";
import {
  Dimmer,
  Dropdown,
  Header,
  Label,
  Loader,
  Message,
  Pagination,
  Segment,
  Table,
  Input,
} from "semantic-ui-react";
import { searchOrders } from "../../libs/requests/notifications";
import { fetchVodafoneNotifications } from "../../libs/requests/vodafoneOrderSearch";
import moment from "moment";
import { Link } from "react-router-dom";

function buildSupplierOptions(isZenOnboarded, isGiganetOnboarded, isVodafoneOnboarded) {
  const opts = [{ key: "all", text: "All suppliers", value: "all" }];
  if (isZenOnboarded && isGiganetOnboarded) opts.push({ key: "zen",      text: "ZEN / Giganet", value: "zen" });
  else if (isZenOnboarded)                  opts.push({ key: "zen",      text: "ZEN",            value: "zen" });
  else if (isGiganetOnboarded)              opts.push({ key: "giganet",  text: "Giganet",         value: "giganet" });
  if (isVodafoneOnboarded)                  opts.push({ key: "vodafone", text: "Vodafone",        value: "vodafone" });
  return opts;
}

const SOURCE_COLOUR = {
  Zen:      "teal",
  Giganet:  "green",
  Vodafone: "red",
};

function parseVodafoneMessage(raw) {
  try {
    const parsed = JSON.parse(raw);
    return parsed.status_message || parsed.order_status || "Update received";
  } catch {
    return raw || "Update received";
  }
}

export default class OrderNotifications extends React.Component {
  static contextType = authContext;

  constructor(props) {
    super(props);
    this.state = {
      notifications: [],
      orders: [],
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,
      isLoading: false,
      searchTerm: "",
      noData: false,
      supplierFilter: "all",
    };
  }

  async componentDidMount() {
    await this.fetchNotifications();
  }

  getFulfilmentStateValue = (statusString) => {
    const statusMap = {
      Pending: 1, Acknowledged: 2, Committed: 4, Completed: 8,
      Rejected: 16, Cancelled: 32, Delayed: 64, Cancelling: 128,
    };
    return statusMap[statusString] || 71;
  };

  buildOrderLinkSearch = (notification) => {
    const params = new URLSearchParams();
    const order = this.state.orders.find(o => o.reference === notification.reference);
    params.append("fulfilmentState", order ? this.getFulfilmentStateValue(order.fulfilmentStatus) : 71);
    if (notification.dateIssued) params.append("orderDate", encodeURIComponent(notification.dateIssued));
    return `?${params.toString()}`;
  };

  fetchNotifications = async () => {
    this.setState({ isLoading: true, noData: false });
    const { searchTerm } = this.state;

    try {
      const isVodafoneOnboarded = this.context?.permissions?.isVodafoneOnboarded ?? false;
      const [result, vodafoneRaw] = await Promise.all([
        searchOrders(searchTerm),
        isVodafoneOnboarded ? fetchVodafoneNotifications().catch(() => []) : Promise.resolve([]),
      ]);

      const allNotifications = [];

      Object.entries(result.zenUpdates).forEach(([key, notifications]) => {
        allNotifications.push(
          ...notifications.map((n) => ({
            reference: key.toUpperCase(),
            message: n.payload?.message || "",
            dateIssued: n.dateIssued,
            type: n.type,
            source: n.source || "Zen",
            author: n.author,
            payload: n.payload,
          }))
        );
      });

      Object.entries(result.giganetUpdates).forEach(([key, notifications]) => {
        allNotifications.push(
          ...notifications.map((n) => ({
            reference: n.giganet_order_reference?.toUpperCase() ?? key.toUpperCase(),
            message: n.comment?.trim() || "",
            dateIssued: n.status_timestamp,
            type: "OrderUpdate",
            source: "Giganet",
            author: "Automated",
            payload: {
              message: n.comment || "",
              subType: n.order_line_update?.order_line_stage || null,
              state: n.state,
              stateReason: null,
            },
          }))
        );
      });

      if (Array.isArray(vodafoneRaw)) {
        vodafoneRaw.forEach((n) => {
          const rawMsg = n.payload?.message || "";
          const displayMsg = parseVodafoneMessage(rawMsg);
          allNotifications.push({
            reference: n.reference?.toUpperCase() ?? "UNKNOWN",
            message: displayMsg,
            dateIssued: n.dateIssued,
            type: n.type || "OrderUpdate",
            source: "Vodafone",
            author: n.author || "Vodafone",
            payload: { message: displayMsg },
          });
        });
      }

      const term = searchTerm?.trim().toLowerCase();
      const validNotifications = allNotifications
        .filter((n) => n.message && n.message.trim() !== "")
        .filter((n) => !term || n.reference?.toLowerCase().includes(term) || n.message?.toLowerCase().includes(term))
        .sort((a, b) => new Date(b.dateIssued) - new Date(a.dateIssued));

      this.setState({
        notifications: validNotifications,
        orders: result.orders || [],
        totalRecords: validNotifications.length,
        isLoading: false,
        noData: validNotifications.length === 0,
      });
    } catch (error) {
      console.error("Error fetching notifications:", error);
      this.setState({ isLoading: false, noData: true });
    }
  };

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value }, this.fetchNotifications);
  };

  handlePageChange = (pageNumber) => {
    this.setState({ currentPage: pageNumber });
  };

  render() {
    const { notifications, currentPage, pageSize, isLoading, noData, searchTerm, supplierFilter } = this.state;
    const { isVodafoneOnboarded = false, isZenOnboarded = false, isGiganetOnboarded = false } = this.context?.permissions ?? {};
    const showSupplierFilter = isVodafoneOnboarded && (notifications.length > 0);
    const supplierOptions = buildSupplierOptions(isZenOnboarded, isGiganetOnboarded, isVodafoneOnboarded);

    const filtered = supplierFilter === "all"
      ? notifications
      : supplierFilter === "vodafone"
        ? notifications.filter(n => n.source === "Vodafone")
        : supplierFilter === "giganet"
          ? notifications.filter(n => n.source === "Giganet")
          : notifications.filter(n => n.source === "Zen" || n.source === "Giganet");

    const startIndex = (currentPage - 1) * pageSize;
    const paginatedNotifications = filtered.slice(startIndex, startIndex + pageSize);
    const totalPages = Math.ceil(filtered.length / pageSize);

    const header = (
      <Header as="h2" textAlign="center" style={{ margin: "20px 0" }}>
        Order Notifications
      </Header>
    );

    return (
      <Segment>
        <Dimmer active={isLoading} inverted>
          <Loader inverted>Loading</Loader>
        </Dimmer>

        <Panel header={header} />

        <div style={{ display: "flex", gap: "16px", alignItems: "center", marginBottom: "12px" }}>
          <Input
            placeholder="Search by reference..."
            value={searchTerm}
            onChange={this.handleSearchChange}
          />
          {showSupplierFilter && supplierOptions.length > 2 && (
            <>
              <span style={{ fontWeight: 600 }}>Supplier:</span>
              <Dropdown
                inline
                options={supplierOptions}
                value={supplierFilter}
                onChange={(_, { value }) => this.setState({ supplierFilter: value, currentPage: 1 })}
              />
            </>
          )}
        </div>

        {noData && <Message>No notifications available.</Message>}

        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Reference</Table.HeaderCell>
              <Table.HeaderCell>Supplier</Table.HeaderCell>
              <Table.HeaderCell style={{ width: "160px" }}>Date</Table.HeaderCell>
              <Table.HeaderCell>Message</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {paginatedNotifications.map((notification, i) => (
              <Table.Row key={`${notification.reference}-${i}`}>
                <Table.Cell>
                  {notification.source === "Vodafone" ? (
                    <Link to={`/VodafoneOrder/${notification.reference}`}>
                      {notification.reference}
                    </Link>
                  ) : (
                    <Link
                      to={{
                        pathname: `/Order/${notification.reference}`,
                        search: this.buildOrderLinkSearch(notification),
                      }}
                    >
                      {notification.reference}
                    </Link>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Label size="small" color={SOURCE_COLOUR[notification.source] ?? "grey"}>
                    {notification.source}
                  </Label>
                </Table.Cell>
                <Table.Cell>
                  {moment(notification.dateIssued).format("YYYY-MM-DD")}
                </Table.Cell>
                <Table.Cell>{notification.payload?.message || notification.message}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        {totalPages > 1 && (
          <Pagination
            totalPages={totalPages}
            activePage={currentPage}
            onPageChange={(_, { activePage }) => this.handlePageChange(activePage)}
          />
        )}
      </Segment>
    );
  }
}
